/**
 * Configuración externa del chat para evitar valores hardcoded
 * Permite cambiar textos, mensajes y comportamientos sin modificar código
 */
export const chatConfig = {
  // 🎨 Textos de la interfaz
  ui: {
    assistant: {
      name: 'Galaxy AI Assistant',
      shortName: 'Galaxy AI',
      company: 'Galaxy Metrics',
      greeting: '¡Hola! Soy tu asistente de Galaxy Metrics',
      description: 'Puedo ayudarte a analizar tus datos publicitarios, revisar presupuestos, interpretar métricas y sugerir optimizaciones.',
      capabilities: 'Pregúntame sobre presupuestos, clientes, campañas o rendimiento',
      status: {
        online: 'Pregúntame sobre tus datos',
        typing: 'Escribiendo...',
        thinking: 'Analizando...'
      }
    },
    
    buttons: {
      toggleChat: 'Abrir/Cerrar chat',
      clearChat: 'Limpiar conversación',
      sendMessage: 'Enviar mensaje',
      generateInsight: 'Generar análisis con IA',
      close: 'Cerrar',
      retry: 'Reintentar'
    },
    
    placeholders: {
      messageInput: 'Escribe tu pregunta aquí...',
      search: 'Buscar en la conversación...',
      loading: 'Cargando...'
    },
    
    labels: {
      conversation: 'Conversación',
      suggestions: 'Preguntas sugeridas:',
      analysis: 'Análisis AI',
      insight: 'Insight generado',
      recommendations: 'Recomendaciones',
      summary: 'Resumen'
    }
  },

  // 🔗 Configuración de endpoints
  endpoints: {
    base: '/chat',
    message: '/message',
    insight: '/insight', 
    compare: '/compare',
    optimize: '/optimize',
    suggestions: '/suggestions',
    health: '/health'
  },

  // ❌ Mensajes de error configurables
  errorMessages: {
    network: 'Error de conexión. Verifica tu conexión a internet.',
    authentication: 'No estás autenticado. Por favor inicia sesión.',
    authorization: 'No tienes permisos para esta acción.',
    rateLimit: 'Demasiadas consultas. Intenta más tarde.',
    serverError: 'Error interno del servidor. Intenta nuevamente.',
    badRequest: 'Solicitud inválida. Verifica los datos enviados.',
    timeout: 'La consulta tardó demasiado. Intenta nuevamente.',
    
    // Errores específicos del chat
    messageEmpty: 'El mensaje no puede estar vacío.',
    messageTooLong: 'El mensaje es demasiado largo.',
    dataAccess: 'No tienes acceso a estos datos.',
    analysisError: 'Error al generar análisis.',
    insightError: 'Error al generar insight.',
    compareError: 'Error al comparar datos.',
    recommendationError: 'Error al generar recomendaciones.',
    
    // Fallbacks
    generic: 'Ha ocurrido un error inesperado.',
    unknown: 'Error desconocido. Contacta al administrador.'
  },

  // ✅ Mensajes de éxito
  successMessages: {
    messageSent: 'Mensaje enviado exitosamente',
    analysisGenerated: '¡Análisis generado exitosamente!',
    chatCleared: 'Conversación limpiada',
    dataLoaded: 'Datos cargados correctamente'
  },

  // ⚠️ Mensajes informativos
  infoMessages: {
    noHistory: 'No hay historial de conversación',
    noSuggestions: 'No hay preguntas sugeridas disponibles',
    connecting: 'Conectando con el asistente...',
    processing: 'Procesando tu consulta...',
    analyzing: 'Analizando los datos...'
  },

  // 🎯 Configuración de comportamiento
  behavior: {
    // Límites
    maxMessageLength: 1000,
    maxHistoryItems: 50,
    maxRetries: 3,
    
    // Timeouts (en milisegundos)
    requestTimeout: 180000, // 3 minutos para consultas complejas de análisis de datos
    typingDelay: 1000,
    retryDelay: 2000,
    
    // Auto-scroll y UX
    autoScroll: true,
    showTypingIndicator: true,
    showTimestamps: true,
    enableSuggestions: true,
    
    // Persistencia
    saveHistory: true,
    autoSaveInterval: 5000
  },

  // 📊 Configuración de análisis
  analysis: {
    defaultObjective: 'mejorar ROI',
    defaultMetric: 'performance',
    
    types: {
      basic: 'Análisis básico',
      detailed: 'Análisis detallado', 
      advanced: 'Análisis avanzado'
    },
    
    dataTypes: {
      budget: 'Presupuesto',
      client: 'Cliente',
      campaign: 'Campaña',
      performance: 'Rendimiento',
      general: 'General'
    },
    
    objectives: {
      roi: 'mejorar ROI',
      cost: 'reducir costos',
      reach: 'aumentar alcance',
      conversions: 'mejorar conversiones',
      efficiency: 'optimizar eficiencia'
    }
  },

  // 🎨 Configuración de estilos (clases CSS)
  styles: {
    theme: 'primary', // primary, secondary, success, etc.
    
    chat: {
      window: 'chat-window',
      content: 'chat-content',
      message: 'chat-message',
      input: 'chat-input'
    },
    
    buttons: {
      primary: 'btn-primary',
      secondary: 'btn-outline-primary',
      small: 'btn-sm',
      large: 'btn-lg'
    },
    
    states: {
      loading: 'loading',
      error: 'error',
      success: 'success',
      typing: 'typing'
    }
  },

  // 🔧 Configuración de desarrollo
  development: {
    enableLogs: process.env.NODE_ENV === 'development',
    enableDebug: false,
    mockResponses: false,
    delaySimulation: 0
  }
};

// 🌍 Configuración de idiomas (preparado para i18n)
export const chatLocales = {
  es: {
    ...chatConfig,
    language: 'es',
    currency: 'USD',
    dateFormat: 'DD/MM/YYYY',
    timeFormat: 'HH:mm'
  },
  
  en: {
    ...chatConfig,
    language: 'en',
    ui: {
      assistant: {
        name: 'Galaxy AI Assistant',
        greeting: 'Hello! I\'m your Galaxy Metrics assistant',
        description: 'I can help you analyze your advertising data, review budgets, interpret metrics and suggest optimizations.',
        capabilities: 'Ask me about budgets, clients, campaigns or performance'
      }
      // ... resto de textos en inglés
    },
    currency: 'USD',
    dateFormat: 'MM/DD/YYYY',
    timeFormat: 'hh:mm A'
  }
};

// 🔄 Función para obtener configuración según idioma
export const getChatConfig = (locale = 'es') => {
  return chatLocales[locale] || chatLocales.es;
};

// 📝 Función para obtener mensajes por categoría
export const getChatMessages = (category, locale = 'es') => {
  const config = getChatConfig(locale);
  return config[category] || {};
};

// 🔗 Función para construir URLs de endpoints
export const buildEndpointUrl = (endpoint, baseUrl = '') => {
  const config = getChatConfig();
  const base = baseUrl || config.endpoints.base;
  const path = config.endpoints[endpoint] || endpoint;
  return `${base}${path}`;
};

export default chatConfig; 